import type { ReactNode } from 'react'
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { useThemeEditorContext } from '../../../hooks/useThemeEditorContext'
import { usePageLayoutContext } from '../../../providers/PageLayoutProvider'
import { BroadcastChannelManager } from '../core/broadcastChannelManager'
import type { PreviewEvents } from '../types'

type ThemeLivePreviewContextType = {
  channel: BroadcastChannelManager<PreviewEvents>
  rect: DOMRect
  isInspectorVisible: boolean
  activeSectionName: string | undefined
  activeSectionKey: string | undefined
  setActiveSectionKey: (currentSectionKey: string | undefined) => void
  setInspectorVisible: (isInspectorVisible: boolean) => void
}

const ThemeLivePreviewContext = createContext<ThemeLivePreviewContextType | undefined>(undefined)

export const ThemeLivePreviewProvider = ({ children }: { children: ReactNode }) => {
  const { currentSectionKey } = useThemeEditorContext()
  const [activeSectionName, setActiveSectionName] = useState<string | undefined>()
  const [activeSectionKey, setActiveSectionKey] = useState<string | undefined>(currentSectionKey)
  const [isInspectorVisible, setInspectorVisible] = useState(false)

  const { getSectionBySectionKey } = usePageLayoutContext()
  const [rect, setRect] = useState<DOMRect>({
    height: 0,
    width: 0,
    x: 0,
    y: 0,
  } as DOMRect)

  const channel = useMemo(() => new BroadcastChannelManager<PreviewEvents>('theme-preview'), [])

  useEffect(() => {
    const handleEvent = (event: MessageEvent<PreviewEvents>) => {
      if (event.data.action === 'ThemePreview:ClearPreview') {
        setActiveSectionKey(undefined)
        setActiveSectionName(undefined)
      }

      if (event.data.action === 'ThemePreview:SectionHover') {
        const key = event.data.payload.id.replace('fw-section-', '')
        const section = getSectionBySectionKey(key)

        setActiveSectionKey(section?.sectionKey)
        setActiveSectionName(section?.name)
        setRect(event.data.payload.rect)
      }

      if (event.data.action === 'ThemePreview:SectionHoverFrame') {
        const key = event.data.payload.id.replace('fw-section-', '')
        const section = getSectionBySectionKey(key)

        setActiveSectionKey(section?.sectionKey)
        setActiveSectionName(undefined)
        setRect(event.data.payload.rect)
      }

      if (event.data.action === 'ThemePreview:Scroll') {
        setRect(event.data.payload.rect)
      }
    }

    channel.on(handleEvent)

    return () => {
      channel.off(handleEvent)
    }
  }, [channel, getSectionBySectionKey])

  const value = useMemo(
    () => ({
      isInspectorVisible,
      channel,
      rect,
      activeSectionName,
      activeSectionKey,
      setActiveSectionKey,
      setInspectorVisible,
    }),
    [activeSectionKey, activeSectionName, channel, isInspectorVisible, rect],
  )

  return (
    <ThemeLivePreviewContext.Provider value={value}>{children}</ThemeLivePreviewContext.Provider>
  )
}

export const useThemeLivePreviewContext = () => {
  const themeLivePreviewContext = useContext(ThemeLivePreviewContext)

  if (themeLivePreviewContext === undefined) {
    throw new Error('themeLivePreviewContext must be used within a ThemeLivePreviewContext')
  }

  return themeLivePreviewContext
}
